import { useCallback } from "react";
import {
  getDevelcoSensorLEDStatus,
  postDevelcoSensorLEDStatus,
  PostDevelcoGatewaySensorsLEDResponseType,
} from "@intelligentlilli/api-layer";
import { SERVER } from "../config";
import { getLEDGuardKey, setCachedLEDState, cleanStaleLEDKeys } from "../Constants";
import * as Sentry from "@sentry/react";

// -- Types --
type Sensor = { id: number; name?: string; label?: string };

// We are using Promise.allSettled instead of Promise.all
type PostResult =
  PromiseSettledResult<PostDevelcoGatewaySensorsLEDResponseType>;

type UseTurnOnSensorLEDsProps = {
  devicestring: string | undefined;
  sensors: any;
  id: string | undefined;
  trackInstall: (props: Record<string, string>) => void;
  userKey: string;
};

// -- Helpers --
const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const POST_DELAY_MS = 2000;
const MAX_ATTEMPTS = 3;
const GUARD_TTL_MS = 900_000; // 15 minutes

const isMotionOrDoor = (device: Sensor) =>
  device.label?.includes("m") || device.label?.includes("d");

const hasAlreadyCheckedLED = (guardKey: string): boolean => {
  try {
    const raw = localStorage.getItem(guardKey);
    if (!raw) return false;
    const { timestamp } = JSON.parse(raw);
    return Date.now() - timestamp < GUARD_TTL_MS;
  } catch {
    localStorage.removeItem(guardKey);
    return false;
  }
};

const allPostResultsOk = (results: PostResult[]) =>
  results.every(
    (r) => r.status === "fulfilled" && r.value.status === 200
  );

const getPostStatuses = (sensors: Sensor[], results: PostResult[]) =>
  results.map((r, i) => ({
    sensorId: sensors[i]?.id,
    label: sensors[i]?.label,
    status: r.status === "fulfilled" ? r.value.status : "rejected",
  }));

/**
 * Attempts to POST LED state for all sensors, retrying up to MAX_ATTEMPTS
 * times with a delay before each attempt.
 */
const postWithRetry = async (
  sensors: Sensor[],
  devicestring: string,
  attempt = 1
): Promise<{ hasSucceeded: boolean; recentResults: PostResult[] }> => {
  console.log(`[LED] Waiting 2s before POST (attempt ${attempt})...`);
  await delay(POST_DELAY_MS);

  console.log(`[LED] Turning on LEDs (attempt ${attempt})...`);
  const results = await Promise.allSettled(
    sensors.map((sensor) =>
      postDevelcoSensorLEDStatus(
        SERVER,
        devicestring,
        String(sensor.id),
        { state: true },
        "web"
      )
    )
  );

  const hasSucceeded = allPostResultsOk(results);
  console.log(
    `[LED] Attempt ${attempt}:`,
    hasSucceeded ? "SUCCESS" : "FAILED",
    getPostStatuses(sensors, results)
  );

  if (hasSucceeded || attempt >= MAX_ATTEMPTS) {
    return { hasSucceeded, recentResults: results };
  }

  console.log("[LED] Retrying...");
  return postWithRetry(sensors, devicestring, attempt + 1);
};

// -- Hook --
export const useTurnOnSensorLEDs = ({
  devicestring,
  sensors,
  id,
  trackInstall,
  userKey,
}: UseTurnOnSensorLEDsProps) => {
  const ledGuardKey = devicestring ? getLEDGuardKey(devicestring) : "";

  const turnOnLEDs = useCallback(async () => {
    if (!devicestring) {
      console.log("[LED] Skipping — no devicestring");
      return;
    }

    // Remove any LED keys older than 24 hours
    cleanStaleLEDKeys();

    if (hasAlreadyCheckedLED(ledGuardKey)) {
      console.log("[LED] Skipping — guard still active");
      return;
    }

    // Set guard before API calls — prevents re-running for 15 min regardless of outcome
    localStorage.setItem(ledGuardKey, JSON.stringify({ timestamp: Date.now() }));

    try {
      const targetSensors: Sensor[] =
        sensors?.devices?.filter(isMotionOrDoor) ?? [];
      
        console.log(
          `[LED] Found ${targetSensors.length} motion/door sensors`,
          targetSensors
        );

      if (targetSensors.length === 0) return;

      // GET check — skip POST if all LEDs are already on
      // We use allSettled instead of Promise.all because we don't want one sensor's API failure to reject the entire batch. 
      // If 4 out of 5 sensors succeed, we still need to know which ones failed rather than losing all results.
      // Promise. allSettled() waits for all promises to settle (resolve/reject) and returns an array of objects describing the outcome of each promise.
      // Promise.all() waits for all promises to resolve and fails if any one of the promises rejects.
      console.log("[LED] Checking current LED status via GET...");
      const getResults = await Promise.allSettled(
        targetSensors.map((sensor) =>
          getDevelcoSensorLEDStatus(SERVER, devicestring, String(sensor.id), "web")
        )
      );

      // Filter to only sensors that aren't already on
      const sensorsNeedingPost = targetSensors.filter((_, i) => {
        const r = getResults[i];
        return !(
          r.status === "fulfilled" &&
          r.value.ok &&
          r.value.data &&
          "data" in r.value.data &&
          r.value.data.data === true
        );
      });

      if (sensorsNeedingPost.length === 0) {
        console.log("[LED] All LEDs already on — skipping POST");
        setCachedLEDState(devicestring, true);
        return;
      }

      console.log(
        `[LED] ${sensorsNeedingPost.length} sensor(s) need POST`,
        sensorsNeedingPost.map((s) => ({ id: s.id, label: s.label }))
      );

      // POST with retry — only for sensors that need it
      const { hasSucceeded, recentResults } = await postWithRetry(
        sensorsNeedingPost,
        devicestring
      );

      if (hasSucceeded) {
        console.log("[LED] All LEDs turned on — writing cache");
        setCachedLEDState(devicestring, true);
      } else {
        trackInstall({
          action: "sensor_LED_turn_on_failed",
          residentId: `${id}`,
        });
        Sentry.withScope((scope) => {
          scope.setTag("Install-API", "postDevelcoSensorLEDStatus");
          Sentry.captureException(
            new Error(
              `WEB - INSTALL postDevelcoSensorLEDStatus - FAIL - ` +
              `resident ID: ${id}, userId: ${userKey}. ` +
              `Failed after ${MAX_ATTEMPTS} attempts. ` +
              `Statuses: ${JSON.stringify(getPostStatuses(sensorsNeedingPost, recentResults))}. ` +
              `At ${new Date().toISOString()}`
            )
          );
        });
      }
    } catch (error) {
      console.error("[LED] Unexpected error in turnOnLEDs:", error);
      Sentry.captureException(error);
    }
  }, [ledGuardKey, devicestring, sensors?.devices, id, trackInstall, userKey]);

  return { turnOnLEDs };
};
